/* eslint-disable import/prefer-default-export, import/no-extraneous-dependencies */
import { Ref, ref } from "@nuxtjs/composition-api";
import { debouncePromise } from "@loadsure/utils";
import { getApiGetUrl } from "@loadsure/core";
import { useCurrentUser } from "./useFireAuth";
import type { AxiosRequestConfig } from "axios";

export const optionalApiTokenProps = () => ({
  apiToken: { type: String, required: false },
  partnerId: { type: String, required: false },
  playground: { type: Boolean, default: false }
});

const isError4xx = (error: any) => {
  if (error === null || error === undefined) return false;
  if (error.response?.status === undefined) return false;
  if (typeof error.response.status !== "number") return false;
  return error.response.status >= 400 && error.response.status < 500;
};

export function useApi(
  context,
  apiToken?: string,
  partnerId?: string,
  playground?: boolean,
  complementUrl = "api"
) {
  const { authUser, userAccess } = useCurrentUser(context);

  const {
    $axios,
    $sentry,
    $config: { apiUrl }
  } = context;

  const getFullUrl = (path: string) =>
    new URL(
      `${complementUrl}${path.startsWith("/") ? "" : "/"}${path}`,
      apiUrl
    ).toString();

  const getAuth = async () => {
    const currPartnerId = partnerId ?? userAccess.value?.partnerId;
    let currToken = apiToken ?? "";
    if (!currToken && authUser.value) {
      currToken = await authUser.value.getIdToken();
    }
    if (currToken) {
      return {
        apiToken: currToken,
        partnerId: currPartnerId
      };
    }
    throw Error("Cannot authorize");
  };

  const getApiUrl = async (path: string, aliasToken = "t", token?: string) => {
    const pathStart = getFullUrl(path);

    const auth = await getAuth();
    return getApiGetUrl(
      pathStart,
      token ?? auth.apiToken,
      auth.partnerId,
      aliasToken
    );
  };

  const getAxiosConfig = async (): Promise<AxiosRequestConfig> => {
    const auth = await getAuth();
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${auth.apiToken}`,
        Playground: `${!!playground}`
      }
    };
    if (auth.partnerId) {
      config.headers = config.headers || {};
      config.headers.PartnerId = auth.partnerId;
    }
    return config;
  };

  const apiGetIsValid = (
    pathFn: () => string,
    isValidFn: (data: any) => boolean,
    setLoading: (value: boolean) => void = () => undefined
  ) => {
    const validator: Ref<() => Promise<boolean> | boolean> = ref(() => true);

    getAxiosConfig().then((config) => {
      const fn = async () => {
        setLoading(true);
        try {
          const url = getFullUrl(pathFn());
          const data = await $axios.$get(url, config);
          return isValidFn(data);
        } catch (error) {
          return false;
        } finally {
          setLoading(false);
        }
      };
      validator.value = debouncePromise(fn, 500);
    });

    return validator;
  };

  async function getApiData(
    path: string,
    responseType?: "blob",
    params?: object,
    throwError = false
  ) {
    try {
      const url = getFullUrl(path);
      const config = await getAxiosConfig();
      config.params = params;
      if (responseType === "blob") {
        config.responseType = "blob";
        return $axios.get(url, config);
      }
      return await $axios.$get(url, config);
    } catch (error: unknown) {
      if (!isError4xx(error)) {
        $sentry?.captureException(error);
      }
      if (throwError) return error;

      const errorResponse = (error as any).response?.data;
      if (errorResponse) return errorResponse;
    }
    return undefined;
  }

  async function postApiData(
    path: string,
    data?: any,
    onUploadProgress: (progressEvent: any) => void = () => undefined,
    contentType?: string,
    throwError = false
  ) {
    try {
      const url = getFullUrl(path);
      const config = await getAxiosConfig();
      if (contentType) {
        config.headers = config.headers || {};
        config.headers["Content-Type"] = contentType;
      }
      const response = await $axios.$post(url, data, {
        ...config,
        onUploadProgress
      });
      return response;
    } catch (error: unknown) {
      if (!isError4xx(error)) {
        $sentry?.captureException(error);
      }
      if (throwError) throw error;

      const errorResponse = (error as any).response?.data;
      if (errorResponse) return errorResponse;
    }
    return undefined;
  }

  async function postUnauthApiData(
    path: string,
    data?: any,
    throwError = false
  ) {
    try {
      const url = getFullUrl(path);
      const response = await $axios.$post(url, data);
      return response;
    } catch (error: unknown) {
      if (!isError4xx(error)) {
        $sentry?.captureException(error);
      }
      if (throwError) throw error;

      const errorResponse = (error as any).response?.data;
      if (errorResponse) return errorResponse;

      return undefined;
    }
  }

  async function putApiData(
    path: string,
    data: any,
    onUploadProgress: (progressEvent: any) => void = () => undefined,
    contentType?: string,
    throwError = false
  ) {
    try {
      const url = getFullUrl(path);
      const config = await getAxiosConfig();
      if (contentType) {
        config.headers = config.headers || {};
        config.headers["Content-Type"] = contentType;
      }
      const response = await $axios.$put(url, data, {
        ...config,
        onUploadProgress
      });
      return response;
    } catch (error: unknown) {
      if (!isError4xx(error)) {
        $sentry?.captureException(error);
      }
      if (throwError) throw error;

      const errorResponse = (error as any).response?.data;
      if (errorResponse) return errorResponse;
    }
    return undefined;
  }

  async function deleteApiData(path: string, throwError = false) {
    try {
      const url = getFullUrl(path);
      const config = await getAxiosConfig();
      const response = await $axios.$delete(url, config);
      return response;
    } catch (error: unknown) {
      if (!isError4xx(error)) {
        $sentry?.captureException(error);
      }
      if (throwError) throw error;

      const errorResponse = (error as any).response?.data;
      if (errorResponse) return errorResponse;
    }
    return undefined;
  }

  const getApiDataRef = (
    path: string,
    mapFn?: (value: any) => any,
    filterFn?: (value: any) => boolean,
    defaultValue: any = [],
    mapResponseFn?: (values: any) => any
  ) => {
    const isLoading: Ref<boolean> = ref(false);
    const loaded: Ref<boolean> = ref(false);
    const items: Ref<any> = ref(defaultValue);
    const httpError: Ref<undefined | Error> = ref();

    function load(newPath?: string) {
      if (!loaded.value) {
        isLoading.value = true;
        getApiData(typeof newPath === "string" ? newPath : path)
          .then((response) => {
            if (response === undefined) {
              throw new Error(
                "The server wasn't able to provide data at this time."
              );
            }

            let newItems = mapResponseFn ? mapResponseFn(response) : response;

            // If mapFn or filterFn is provided, ensure we have an array
            if ((mapFn || filterFn) && !Array.isArray(newItems)) {
              throw new Error(
                "The server provided data that was not in the expected format."
              );
            }

            if (mapFn) {
              newItems = newItems.map(mapFn);
            }

            if (filterFn) {
              newItems = newItems.filter(filterFn);
            }

            items.value = newItems;
            loaded.value = true;
          })
          .catch((error) => {
            items.value = defaultValue;
            if (error instanceof Error) {
              httpError.value = error;
            } else if (typeof error === "string") {
              httpError.value = new Error(error);
            } else {
              httpError.value = new Error(String(error));
            }
            // Record any errors that were not handled by the axios-level catch (map/filter)
            $sentry?.captureException(`API Error ${path}: ${error}`);
          })
          .finally(() => {
            isLoading.value = false;
          });
      }

      return items;
    }

    return {
      isLoading,
      loaded,
      items,
      load,
      error: httpError
    };
  };

  return {
    apiGetIsValid,
    getApiUrl,
    getApiData,
    postApiData,
    putApiData,
    getApiDataRef,
    deleteApiData,
    getFullUrl,
    postUnauthApiData
  };
}
